import { getIdToken } from './auth';

const tournamentId = process.env.NEXT_PUBLIC_TOURNAMENT_ID;

export interface QRHuntProps {
  tournamentId: string;
  day: string;
  hole: number;
  slot: number;
}

export interface Result {
  tournamentId: string;
  day: string;
  playerId: string;
  playerName: string;
  score: number;
  hole: number;
  actualScore: number;
  inBonusPeriod: boolean;
  multiplier: number;
  teamName: string;
  createdAt: Date;
}
export interface BonusPeriod {
  tournid: string;
  open: boolean;
  closingAt: Date;
  multiplier: number;
  countdown: number;
  // history: { type: Array<any>, default: [] },
}
export interface AvailableHole {
  number: number;
  name: string;
  location: string;
  image: string;
  par: number;
  yards: number;
}

export interface Day {
  name: string;
  id: string;
  tournamentId: string;
  image: string;
  hole: AvailableHole;
  date: string;
}

export interface Leaderboard {
  username: string;
  score: number;
  avatar: string;
  percentage: number;
  rank: number;
  initials: string;
}

export interface Vote {
  playerId: string;
  teamId?: string;
  score: number;
}

export interface VoteResponse {
  data: {
    success: boolean;
    message?: string;
  };
}

export async function voteForPlayers({
  votes,
  day,
  hole,
}: {
  votes: Vote[];
  day: string;
  hole: number;
}): Promise<VoteResponse> {
  try {
    // Get the current user's ID token
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    // Try different ways to access the environment variable
    const envVar = process.env.NEXT_PUBLIC_API_BASE_URL;
    const baseUrl = envVar || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/golf/vote_for_players`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({
        votes,
        day,
        tournamentId,
        hole,
      }),
    });

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error submitting votes:', error);
    throw error;
  }
}

export async function voteForPlayer({
  playerId,
  day,
  score,
  hole,
}: {
  playerId: string;
  day: string;
  tournamentId: string;
  score: number;
  hole: number;
}): Promise<VoteResponse> {
  // Use the new voteForPlayers function with a single vote
  return voteForPlayers({
    votes: [{ playerId, score }],
    day,
    hole,
  });
}

export async function getLeaderboard(): Promise<{
  data: Leaderboard[];
  day: string;
}> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    const envVar = process.env.NEXT_PUBLIC_API_BASE_URL;
    const baseUrl = envVar || 'http://localhost:3008';

    const url = `${baseUrl}/api/golf/leaderboard?day=&tournamentId=${tournamentId}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
    });

    const data = await response.json();

    if (data.data && Array.isArray(data.data)) {
      data.data = data.data.map((hole: AvailableHole) => ({
        ...hole,
      }));
    }

    return data;
  } catch (error) {
    console.error('Error fetching available holes:', error);
    throw error;
  }
}

export async function getAvailableDays(): Promise<{ data: Day[] }> {
  try {
    // Get the current user's ID token
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    // Try different ways to access the environment variable
    const envVar = process.env.NEXT_PUBLIC_API_BASE_URL;
    const baseUrl = envVar || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/available_days${tournamentId ? `?tournamentId=${tournamentId}` : ''}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    const data = await response.json();

    // Transform the image URLs to use our proxy
    if (data.data && Array.isArray(data.data)) {
      data.data = data.data.map((day: Day) => ({
        ...day,
      }));
    }

    return data;
  } catch (error) {
    console.error('Error fetching available holes:', error);
    throw error;
  }
}

export async function getAvailableHoles(): Promise<{ data: AvailableHole[] }> {
  try {
    // Get the current user's ID token
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    // Try different ways to access the environment variable
    const envVar = process.env.NEXT_PUBLIC_API_BASE_URL;
    const baseUrl = envVar || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/available_holes?tournamentId=${tournamentId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    const data = await response.json();
    // Transform the image URLs to use our proxy
    if (data.data && Array.isArray(data.data)) {
      data.data = data.data.map((hole: AvailableHole) => ({
        ...hole,
      }));
    }

    return data;
  } catch (error) {
    console.error('Error fetching available holes:', error);
    throw error;
  }
}

export interface HoleScore {
  round: number;
  vspar: number;
  finished: boolean;
  _id: string;
}

export interface Player {
  name: string;
  id: string;
  holeScores: (number | HoleScore)[];
  current_hole_vs_par?: number;
  _id: string;
  image: string;
  vote?: {
    _id: string;
    playerId: string;
    score: number;
    day: string;
    hole: number;
    tournamentId: string;
    uid: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
  score?: {
    _id: string;
    day: string;
    playerId: string;
    tournamentId: string;
    hole: number;
    score: number;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

export interface Team {
  teamName: string;
  currentHole: number;
  players: Player[];
  name: string;
  votingOpen: boolean;
  hasVoted?: boolean;
  teamScore?: number;
  minScore?: number;
  maxScore?: number;
  percentage?: number;
  order: number;
  id: string;
  allPlayersScored: boolean;
}

export async function getTeamsByHole(
  holeNumber: string,
  test: string
): Promise<{ teams: Team[] }> {
  try {
    // Get the current user's ID token
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/teams_by_hole?hole=${holeNumber}&tournamentId=${tournamentId}${test ? `&test=${test}` : ''}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    return { teams: responseData.data };
  } catch (error) {
    console.error('Error fetching teams by hole:', error);
    throw error;
  }
}

export async function getTeams({
  votingOpen,
  hole,
  day,
}: {
  votingOpen: boolean;
  hole: number;
  day: string;
}): Promise<{ teams: Team[] }> {
  try {
    // Get the current user's ID token
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';
    const response = await fetch(
      `${baseUrl}/api/golf/teams?hole=${hole}&tournamentId=${tournamentId}&day=${day}&votingOpen=${votingOpen}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    return { teams: responseData.data };
  } catch (error) {
    console.error('Error fetching teams by hole:', error);
    throw error;
  }
}

export async function getUserTotalScore(): Promise<{
  data: { totalScore: number };
}> {
  try {
    // Get the current user's ID token
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/user_total_score?tournamentId=${tournamentId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    return { data: responseData.data };
  } catch (error) {
    console.error('Error fetching user total score:', error);
    throw error;
  }
}

export async function getBonusPeriod(): Promise<BonusPeriod> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/bonus_period?tournid=${tournamentId}`,
      {
        headers: {
          'auth-token': token,
        },
      }
    );

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Error fetching bonus period:', error);
    throw error;
  }
}

export async function getMyResults({
  tournamentId,
}: {
  tournamentId: string;
}): Promise<{ data: Result[] }> {
  const envVar = process.env.NEXT_PUBLIC_API_BASE_URL;
  const baseUrl = envVar || 'http://localhost:3008';
  const token = await getIdToken();

  if (!token) {
    throw new Error('User not authenticated');
  }

  return fetch(`${baseUrl}/api/golf/my_results?tournamentId=${tournamentId}`, {
    headers: {
      'Content-Type': 'application/json',
      'auth-token': token,
    },
  }).then(res => res.json());
}

export async function getBonusPeriodStatus(): Promise<{ open: boolean }> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/bonus_period_status?tournamentId=${tournamentId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching bonus period status:', error);
    return { open: false };
  }
}

export interface BackNineHole {
  id: number;
  name: string;
  par: number;
  yards: number;
  score?: number;
  hasVoted?: boolean;
  result?: number;
  correctResult?: number;
  hasResult?: boolean;
}

export interface BackNinePlayer {
  id: string;
  name: string;
  image?: string;
}

export interface BackNineGameData {
  player: BackNinePlayer;
  holes: BackNineHole[];
  hasVoted?: boolean;
  totalScore?: number;
  totalHoles?: number;
  accuracy?: string;
  resultsRecordedAt?: string;
  playerFinishedAt?: string;
  open?: boolean;
}

export async function getBackNineGameData({
  tournamentId,
  day,
}: {
  tournamentId: string;
  day: string;
}): Promise<BackNineGameData> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/back-nine/game-data?tournamentId=${tournamentId}&day=${day}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching back-nine game data:', error);
    throw error;
  }
}

export async function getBackNineGameStatus({
  tournamentId,
  day,
}: {
  tournamentId: string;
  day: string;
}): Promise<{ open: boolean }> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/back-nine/game-status?tournamentId=${tournamentId}&day=${day}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching back-nine game status:', error);
    // Default to closed if there's an error
    return { open: false };
  }
}

export async function getBackNineSelectedDay(): Promise<{
  success: boolean;
  day: number;
}> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/selected_day?tournamentId=${tournamentId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching back-nine selected day:', error);
    // Return day 0 on error to show day selector
    return { success: false, day: 0 };
  }
}

export interface LockPredictionVote {
  id: number;
  score: number;
}

export interface LockPredictionRequest {
  votes: LockPredictionVote[];
  day: number;
  tournamentId: string;
  playerId: string;
}

export async function lockPrediction({
  votes,
  day,
  tournamentId,
  playerId,
}: LockPredictionRequest): Promise<BackNineGameData> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/back-nine/lock-prediction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({
        votes,
        day,
        tournamentId,
        playerId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Transform the API response to match BackNineGameData structure
    const gameData: BackNineGameData = {
      player: data.player,
      holes: data.holes,
      hasVoted: true, // Always true after locking
      totalScore: data.totalScore,
      totalHoles: data.totalHoles,
      accuracy: data.accuracy?.toString() || '0',
      resultsRecordedAt: data.resultsRecordedAt,
      playerFinishedAt: data.playerFinishedAt,
    };

    return gameData;
  } catch (error) {
    console.error('Error locking predictions:', error);
    throw error;
  }
}

// Back Nine Config API Functions
export interface BackNineConfig {
  _id?: string;
  id?: string;
  tournamentId: string;
  playerId: string;
  day: number;
  open?: boolean;
  openAt?: Date;
  closedAt?: Date;
  playerName?: string;
  history?: Array<{
    open: boolean;
    openAt?: Date;
    closedAt?: Date;
  }>;
  createdAt?: Date;
  updatedAt?: Date;
}

export async function getBackNineConfigList(): Promise<BackNineConfig[]> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/back_nine_config_list?tournamentId=${tournamentId}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching back nine config list:', error);
    throw error;
  }
}

export async function getBackNineConfig({
  tournamentId,
  playerId,
  day,
}: {
  tournamentId: string;
  playerId: string;
  day: number;
}): Promise<BackNineConfig | null> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/back_nine_config?tournamentId=${tournamentId}&playerId=${playerId}&day=${day}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching back nine config:', error);
    throw error;
  }
}

export async function createBackNineConfig(
  config: BackNineConfig
): Promise<BackNineConfig> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/back_nine_config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating back nine config:', error);
    throw error;
  }
}

export async function updateBackNineConfig({
  id,
  tournamentId,
  playerId,
  day,
}: {
  id: string;
  tournamentId: string;
  playerId: string;
  day: number;
}): Promise<BackNineConfig> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/edit_back_nine_config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ id, tournamentId, playerId, day }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating back nine config:', error);
    throw error;
  }
}

export async function openBackNineConfig(id: string): Promise<BackNineConfig> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/open_back_nine_config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ id }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error opening back nine config:', error);
    throw error;
  }
}

export async function closeBackNineConfig(id: string): Promise<BackNineConfig> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/close_back_nine_config`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({ id }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error closing back nine config:', error);
    throw error;
  }
}

// Back Nine Scoring API Functions
export interface BackNineScore {
  _id?: string;
  id?: string;
  tournamentId: string;
  playerId: string;
  day: number;
  hole: number;
  score: number;
}

export async function getBackNineScores({
  tournamentId,
  day,
  playerId,
}: {
  tournamentId: string;
  day: number;
  playerId: string;
}): Promise<BackNineScore[]> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/back_nine_scores?tournamentId=${tournamentId}&day=${day}&playerId=${playerId}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching back nine scores:', error);
    throw error;
  }
}

export async function submitBackNineScore({
  tournamentId,
  day,
  playerId,
  hole,
  score,
}: BackNineScore): Promise<BackNineScore> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/submit_back_nine_score`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({ tournamentId, day, playerId, hole, score }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error submitting back nine score:', error);
    throw error;
  }
}

export async function editBackNineScore({
  id,
  score,
}: {
  id: string;
  score: number;
}): Promise<BackNineScore> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/edit_back_nine_score`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ id, score }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error editing back nine score:', error);
    throw error;
  }
}

export interface BackNineScoreByDay {
  uid: string;
  score: {
    totalScore: number;
    totalHoles: number;
    accuracy: number;
  };
  name: string;
  email: string;
}

export async function getBackNineScoresByDay({
  tournamentId,
  day,
}: {
  tournamentId: string;
  day: string;
}): Promise<BackNineScoreByDay[]> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/get_back_nine_scores_by_day?tournamentId=${tournamentId}&day=${day}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching back nine scores by day:', error);
    throw error;
  }
}

// Quiz API Functions
export interface QuizOption {
  id: string;
  text: string;
  chineseText: string;
  arabicText: string;
}

export interface QuizQuestion {
  question: string;
  chineseQuestion: string;
  arabicQuestion: string;
  options: QuizOption[];
  correctAnswerId: string;
  explanation?: string;
  chineseExplanation?: string;
  arabicExplanation?: string;
}

export interface Quiz {
  _id?: string;
  id?: string;
  tournamentId: string;
  day: number;
  questions: QuizQuestion[];
  createdAt?: Date;
  updatedAt?: Date;
}

export async function createQuiz({
  tournamentId,
  day,
  questions,
}: {
  tournamentId: string;
  day: number;
  questions: QuizQuestion[];
}): Promise<Quiz> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/quiz-admin/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ tournamentId, day, questions }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating quiz:', error);
    throw error;
  }
}

export async function editQuiz({
  id,
  tournamentId,
  day,
  questions,
}: {
  id: string;
  tournamentId: string;
  day: number;
  questions: QuizQuestion[];
}): Promise<Quiz> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/quiz-admin/edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ id, tournamentId, day, questions }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error editing quiz:', error);
    throw error;
  }
}

export async function getQuizList({
  tournamentId,
  day,
}: {
  tournamentId?: string;
  day?: number;
} = {}): Promise<Quiz[]> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    let url = `${baseUrl}/api/admin/quiz-admin/list`;
    const params = new URLSearchParams();
    if (tournamentId) params.append('tournamentId', tournamentId);
    if (day !== undefined) params.append('day', day.toString());
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'auth-token': token,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting quiz list:', error);
    throw error;
  }
}

export async function getQuiz({ id }: { id: string }): Promise<Quiz> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/quiz-admin/get?id=${id}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting quiz:', error);
    throw error;
  }
}

export async function deleteQuiz({ id }: { id: string }): Promise<void> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/quiz-admin/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ id }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting quiz:', error);
    throw error;
  }
}

// Health Quiz API Functions
export interface QuizAnswer {
  questionIndex: number;
  answerId: string;
}

export interface QuizEntry {
  _id?: string;
  uid?: string;
  tournamentId: string;
  day: number;
  entries?: Array<{
    questionId: string;
    answerId: string;
    isCorrect: boolean;
  }>;
  score?: number;
  completed?: boolean;
  completedAt?: Date;
  startedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export async function checkHealthQuizStatus(): Promise<HealthQuizStatus | null> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/quiz/health_quiz_status`, {
      method: 'GET',
      headers: {
        'auth-token': token,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error checking health quiz status:', error);
    throw error;
  }
}

export async function getHealthQuiz({
  tournamentId,
  day,
}: {
  tournamentId: string;
  day: number;
}): Promise<{
  tournamentId: string;
  day: number;
  questions: Array<{
    index: number;
    question: string;
    chineseQuestion: string;
    arabicQuestion: string;
    options: Array<{
      id: string;
      text: string;
      chineseText: string;
      arabicText: string;
    }>;
    explanation?: string;
    chineseExplanation?: string;
    arabicExplanation?: string;
  }>;
  completed: boolean;
  entryId?: string;
  totalQuestions?: number;
  answeredQuestions?: number;
}> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/quiz/get?tournamentId=${tournamentId}&day=${day}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting health quiz:', error);
    throw error;
  }
}

export async function submitHealthQuiz({
  tournamentId,
  day,
  answers,
}: {
  tournamentId: string;
  day: number;
  answers: QuizAnswer[];
}): Promise<QuizEntry> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/quiz/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ tournamentId, day, answers }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error submitting health quiz:', error);
    throw error;
  }
}

export async function submitQuizAnswer({
  tournamentId,
  day,
  questionIndex,
  answerId,
}: {
  tournamentId: string;
  day: number;
  questionIndex: number;
  answerId: string;
}): Promise<{
  isCorrect: boolean;
  correctCount: number;
  incorrectCount: number;
  totalQuestions: number;
  answeredQuestions: number;
  completed: boolean;
  score: number;
  correctAnswerId?: string;
  explanation?: {
    text?: string;
    chineseText?: string;
    arabicText?: string;
  };
}> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/quiz/submit-answer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({ tournamentId, day, questionIndex, answerId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error submitting quiz answer:', error);
    throw error;
  }
}

export async function getQuizProgress({
  tournamentId,
  day,
}: {
  tournamentId: string;
  day: number;
}): Promise<{
  hasStarted: boolean;
  entries: Array<{
    questionId: string;
    answerId: string;
    isCorrect: boolean;
  }>;
  score: number;
  correctCount: number;
  incorrectCount: number;
  completed: boolean;
  progress: number;
  totalQuestions: number;
  answeredQuestions: number;
}> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/quiz/progress?tournamentId=${tournamentId}&day=${day}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting quiz progress:', error);
    throw error;
  }
}

export async function getHealthQuizResults({
  tournamentId,
  day,
}: {
  tournamentId?: string;
  day?: number;
} = {}): Promise<QuizEntry[]> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    let url = `${baseUrl}/api/quiz/results`;
    const params = new URLSearchParams();
    if (tournamentId) params.append('tournamentId', tournamentId);
    if (day !== undefined) params.append('day', day.toString());
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'auth-token': token,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting health quiz results:', error);
    throw error;
  }
}

export interface QuizEntryExport {
  score: number;
  questionsAnswered: number;
  questionsCorrect: number;
  questionsIncorrect: number;
  questionsTotal: number;
  questionsPercentage: number;
  name: string;
  email: string;
}

export async function getQuizEntries({
  tournamentId,
  day,
}: {
  tournamentId: string;
  day: string;
}): Promise<QuizEntryExport[]> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/quiz-admin/get_quiz_entries?tournamentId=${tournamentId}&day=${day}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching quiz entries:', error);
    throw error;
  }
}

// WeChat API functions
export async function saveWeChatNumber(
  uid: string,
  wechatNumber: string
): Promise<void> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/users/wechat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({
        uid,
        wechatNumber,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    await response.json();
  } catch (error) {
    console.error('Error saving WeChat number:', error);
    throw error;
  }
}

export async function getWeChatNumber(uid: string): Promise<string | null> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/users/wechat?uid=${uid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // WeChat number not found is not an error, just return null
        return null;
      }
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data.data?.wechatNumber || null;
  } catch (error) {
    console.error('Error getting WeChat number:', error);
    throw error;
  }
}


export async function hasCheckedIn(payload: QRHuntProps): Promise<boolean> {
  const baseUrl =
    process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';
  const token = await getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }

  // Ensure all values are available/valid
  if (
    !payload.tournamentId ||
    !payload.day ||
    !payload.hole ||
    payload.slot === undefined
  ) {
    throw new Error('Missing payload fields for check-in verification.');
  }

  const params = new URLSearchParams({
    tournamentId: payload.tournamentId,
    day: String(payload.day),
    hole: String(payload.hole),
    slot: String(payload.slot),
  });

  const response = await fetch(
    `${baseUrl}/api/qrhunt/entry?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
    }
  );

  if (!response.ok) {
    // If 404 means not checked in yet, so return false
    if (response.status === 404) {
      return false;
    }
    throw new Error(`Failed to fetch check-in status: ${response.status}`);
  }

  const data = await response.json();
  // If there is a record (data.data not empty/null/undefined), user has checked in.
  return !!(data && data.data && Array.isArray(data.data)
    ? data.data.length > 0
    : data.data);
}

export async function checkIn(payload: QRHuntProps) {
  const baseUrl =
    process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';
  const token = await getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }

  const response = await fetch(`${baseUrl}/api/qrhunt/entry`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'auth-token': token,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Check-in failed with status ${response.status}`);
  }

  return response.json();
}

// Selected Day API Functions
export interface SelectedDay {
  _id?: string;
  day: number | null;
  tournamentId: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export async function getSelectedDay(): Promise<SelectedDay | null> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/selected_day?tournamentId=${tournamentId}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      // If 404, return null (no day selected)
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching selected day:', error);
    throw error;
  }
}

export async function selectDay(day: number): Promise<SelectedDay> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }
    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/select_day`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': token,
      },
      body: JSON.stringify({
        tournamentId,
        day: day,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error selecting day:', error);
    throw error;
  }
}

export interface HealthQuizStatus {
  _id?: string;
  open: boolean;
}

export async function getHealthQuizStatus(): Promise<HealthQuizStatus> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(`${baseUrl}/api/admin/health_quiz_status`, {
      method: 'GET',
      headers: {
        'auth-token': token,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error getting health quiz status:', error);
    throw error;
  }
}

export async function toggleHealthQuizStatus(
  open: boolean
): Promise<HealthQuizStatus> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/admin/toggle_health_quiz_status`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({ open }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error toggling health quiz status:', error);
    throw error;
  }
}

export async function countAllVotesFromDay(
  tournamentId: string,
  day: string
): Promise<number> {
  try {
    const token = await getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    const baseUrl =
      process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3008';

    const response = await fetch(
      `${baseUrl}/api/golf/count_all_votes_from_day?tournamentId=${tournamentId}&day=${day}`,
      {
        method: 'GET',
        headers: {
          'auth-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error counting votes from day:', error);
    throw error;
  }
}
